import { ArrowDropDown, SearchOutlined } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Chip,
  ClickAwayListener,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { grey, indigo } from "../../../../colors";
import useFilterOptions from "../../../../contexts/useFilterOptions";
import { ClarifyingQuestion } from "../../models";
import { MAX_PREDEFINED_OPTIONS, OTHER_OPTION, QuestionSelection, readableHint } from "./types";

export default function QuestionOptions({
  question,
  selection,
  disabled,
  onToggle,
  onToggleMulti,
  onOtherText,
}: {
  question: ClarifyingQuestion;
  selection: QuestionSelection;
  disabled: boolean;
  onToggle: (option: string) => void;
  // Always-multi-select toggle used by the searchable dropdown.
  onToggleMulti: (option: string) => void;
  onOtherText: (text: string) => void;
}) {
  const otherSelected = selection.selected.has(OTHER_OPTION);
  const ControlComponent = question.multi_select ? Checkbox : Radio;
  const otherInputRef = useRef<HTMLInputElement>(null);

  const { data: globalFilters } = useFilterOptions();

  // When the question's field_hint matches a known facet, render a searchable multi-select
  // dropdown backed by the full facet value list. Otherwise fall back to the LLM's short
  // option list rendered as radio/checkbox rows (unchanged behaviour).
  const facetOptions = useMemo(() => {
    const hint = question.field_hint;
    if (!globalFilters || !hint) return null;
    const facet = globalFilters.common_filters?.find((f) => f.column_name === hint);
    const values = facet?.filter_values;
    if (!values || values.length === 0) return null;
    return [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [globalFilters, question.field_hint]);

  const options = (question.options ?? []).slice(0, MAX_PREDEFINED_OPTIONS);

  return (
    <Stack spacing={0.5}>
      {facetOptions ? (
        <SearchableDropdown
          options={facetOptions}
          selection={selection}
          disabled={disabled}
          fieldHint={question.field_hint}
          onToggle={onToggleMulti}
        />
      ) : (
        options.map((option) => (
          <OptionRow
            key={option}
            label={option}
            selected={selection.selected.has(option)}
            disabled={disabled}
            control={
              <ControlComponent
                checked={selection.selected.has(option)}
                onChange={() => onToggle(option)}
                disabled={disabled}
                size="small"
              />
            }
          />
        ))
      )}

      {/* "Other" free-text row: typing auto-selects it; picking another option greys it out as a preserved draft. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          borderRadius: "8px",
          border: `1px solid ${otherSelected ? indigo[300] : grey[200]}`,
          backgroundColor: otherSelected ? "#fff" : "transparent",
          px: "0.25em",
          transition: "border-color 0.15s ease, background-color 0.15s ease",
          "&:hover": disabled ? {} : { borderColor: indigo[300], backgroundColor: "#fff" },
        }}
      >
        <ControlComponent
          checked={otherSelected}
          // Selection is driven by onClick below; React still requires onChange when `checked` is set.
          onChange={() => {}}
          onClick={() => {
            if (disabled) return;
            onToggle(OTHER_OPTION);
            otherInputRef.current?.focus();
          }}
          disabled={disabled}
          size="small"
          sx={{ p: "0.25em", ml: "0.25em" }}
        />
        <TextField
          fullWidth
          variant="standard"
          placeholder="Other — type your own answer…"
          value={selection.otherText}
          disabled={disabled}
          onChange={(event) => onOtherText(event.target.value)}
          inputRef={otherInputRef}
          InputProps={{ disableUnderline: true }}
          sx={{
            "& .MuiInputBase-input": {
              py: "0.5em",
              fontSize: "0.875rem",
              lineHeight: 1,
              color: selection.otherText && !otherSelected ? grey[400] : grey[800],
            },
          }}
        />
      </Box>
    </Stack>
  );
}

function OptionRow({
  label,
  selected,
  disabled,
  control,
}: {
  label: string;
  selected: boolean;
  disabled: boolean;
  control: React.ReactElement;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        borderRadius: "8px",
        border: `1px solid ${selected ? indigo[300] : grey[200]}`,
        backgroundColor: selected ? "#fff" : "transparent",
        px: "0.25em",
        transition: "border-color 0.15s ease, background-color 0.15s ease",
        "&:hover": disabled ? {} : { borderColor: indigo[300], backgroundColor: "#fff" },
      }}
    >
      <FormControlLabel
        control={control}
        label={
          <Typography variant="body2" color={grey[800]} sx={{ lineHeight: 1 }}>
            {label}
          </Typography>
        }
        sx={{ m: 0, py: "0.2em", flex: 1, "& .MuiButtonBase-root": { p: "0.25em", ml: "0.25em" } }}
      />
    </Box>
  );
}

// Searchable, multi-select dropdown backed by facet values.
function SearchableDropdown({
  options,
  selection,
  disabled,
  fieldHint,
  onToggle,
}: {
  options: string[];
  selection: QuestionSelection;
  disabled: boolean;
  fieldHint: string;
  onToggle: (option: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const hint = readableHint(fieldHint);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const lower = search.toLowerCase();
    return options.filter((opt) => opt.toLowerCase().includes(lower));
  }, [options, search]);

  // Selected facet values, excluding the "Other" sentinel which is handled by its own row.
  const selectedValues = useMemo(
    () => Array.from(selection.selected).filter((v) => v !== OTHER_OPTION),
    [selection.selected],
  );

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative" }}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          placeholder={`Search or select a ${hint}`}
          value={search}
          onClick={() => !disabled && setOpen(true)}
          onChange={(event) => {
            setSearch(event.target.value);
            if (!open) setOpen(true);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined sx={{ fontSize: 20, color: indigo[500] }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!disabled) setOpen((prev) => !prev);
                  }}
                  sx={{ p: "0.15em" }}
                >
                  <ArrowDropDown
                    sx={{
                      color: grey[500],
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: open ? "8px 8px 0 0" : "8px",
              backgroundColor: "#fff",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: grey[200] },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: indigo[300] },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: indigo[300], borderWidth: 1 },
            },
          }}
        />
        <Collapse in={open && !disabled}>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${grey[200]}`,
              borderTop: "none",
              borderRadius: "0 0 8px 8px",
              maxHeight: 220,
              overflowY: "auto",
              backgroundColor: "#fff",
            }}
          >
            {filtered.length === 0 && (
              <Box px="1em" py="0.75em">
                <Typography variant="body2" color={grey[500]}>
                  No options found
                </Typography>
              </Box>
            )}
            {filtered.map((option) => (
              <Box
                key={option}
                onClick={() => onToggle(option)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: "0.75em",
                  py: "0.35em",
                  cursor: "pointer",
                  borderBottom: `1px solid ${grey[100]}`,
                  "&:hover": { backgroundColor: indigo[50] },
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Checkbox
                  checked={selection.selected.has(option)}
                  size="small"
                  tabIndex={-1}
                  disableRipple
                  sx={{ p: "4px", mr: "0.5em", color: grey[400], "&.Mui-checked": { color: indigo[500] } }}
                />
                <Typography variant="body2" color={grey[800]}>
                  {option}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Collapse>

        {/* Selected values shown as chips below the closed dropdown, mirroring the current UI. */}
        {!open && selectedValues.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap="0.3em" mt="0.4em">
            {selectedValues.map((value) => (
              <Chip
                key={value}
                label={value}
                size="small"
                onDelete={disabled ? undefined : () => onToggle(value)}
                sx={{
                  backgroundColor: indigo[100],
                  color: indigo[700],
                  "& .MuiChip-deleteIcon": { color: indigo[400], "&:hover": { color: indigo[700] } },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
}
